import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Creators as getProfileMastersDataActions } from '@/domain/useCases/getProfileMastersData';
import {
  PROVINCES as province,
  DEPARTMENTS as department,
  DISTRICTS as district,
  country
} from '@/presentation/modules/procedures/modules/scholarships/bse/forms/datos/constants/ubigeo';
import Input from '@/presentation/reusables/Input';
import ReusableButton from '@/presentation/reusables/Button';
import ReusableInfo from '@/presentation/reusables/Info';
import {
  Row,
  SInputToggle,
  SInputToggleInput,
  SInputToggleLabel
} from './styled';
import InputText from '@/presentation/modules/procedures/modules/scholarships/bse/components/InputText';
import Select from '@/presentation/modules/procedures/modules/scholarships/bse/components/Select';
import ModalConfirmation from '@/presentation/modules/procedures/components/Modal/Modal';
import ContentModalTwoButtons from '@/presentation/modules/procedures/components/ContentModalTwoButtons';
import TextHelper from '@/core/helpers/TextHelper';
import InputPhone from '@/presentation/modules/procedures/modules/scholarships/bse/components/InputPhone';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';

type MaritalStatus = {
  id: string;
  name: string;
};

type MaritalPais = {
  COUNTRY: string;
  DESCR: string;
};

type MaritalDepartamento = {
  state: string;
  DESCR: string;
};

type AdaptedMaritalStatus = {
  id: string;
  description: string;
};

interface Country {
  COUNTRY: string;
  DESCR: string;
}

interface Department {
  id: number;
  description: string;
  state: string;
  DESCR: string;
}

interface Province {
  state: string;
  MBO_PROVINCIA: string;
  DESCR: string;
  id: number;
  departmentId: number;
  description: string;
}

interface District {
  state: string;
  MBO_PROVINCIA: string;
  MBO_DISTRITO: string;
  DESCR: string;
  id: number;
  provinceId: number;
  description: string;
}

const InputToggle = ({
  actionState,
  inputType,
  profile,
  profileData,
  fullName,
  controllerActionState,
  hasActionCrud = true,
  defaultValue,
  isLoadingMasters
}: any) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [textPermission, setTextPermission] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  // eslint-disable-next-line
  const [isValid, setIsValid] = useState<boolean>(false);
  const [value, setValue] = useState<any>({});
  const [errorForm, setErrorForm] = useState<any>({});
  const [openModal, setOpenModal] = useState(false);
  const [initialForm, setInitialForm] = useState<any>();
  const [formChanges, setFormChanges] = useState<any>();

  const dispatch = useDispatch();
  const { data } = useSelector((state: any) => state?.getProfileMastersData);

  const formatDate = (date: string) => {
    if (date === '' || date === undefined || date === null) {
      return '';
    }
    const partesFecha = date.split('-');
    const fechaFormateada = `${partesFecha[2]}/${partesFecha[1]}/${partesFecha[0]}`;
    return fechaFormateada;
  };

  function getDepartmentsByCountry(countryCode: string) {
    if (value.Pais) {
      const currentCountry = (country as Country[]).find(
        (cont) => cont.DESCR === value.Pais
      );
      if (currentCountry) {
        setValue({ ...value, Pais: currentCountry.COUNTRY });
      }
    }
    if (countryCode === 'PER' || countryCode === 'Perú') {
      return department;
    }
    return [];
  }

  function getProvinceByDepartments(departments: any) {
    if (value.Departamento) {
      const currentDepartment = (department as Department[]).find(
        (dpto) => dpto.DESCR === value.Departamento.toUpperCase()
      );
      if (currentDepartment) {
        setValue({ ...value, Departamento: currentDepartment.state });
        return (province as Province[])
          .filter((prov) => prov.state === currentDepartment.state)
          .map((item) => ({
            id: item.MBO_PROVINCIA,
            description: TextHelper.capitalize(item.DESCR)
          }));
      }
    }
    if (departments !== '') {
      return (province as Province[])
        .filter((item) => item.state === departments)
        .map((item) => ({
          id: item.MBO_PROVINCIA,
          description: TextHelper.capitalize(item.DESCR)
        }));
    }
    return [];
  }

  function getDistrictByProvince(distrito: any) {
    if (value.Provincia && value.Provincia !== distrito) {
      const currentProvince = (province as Province[]).find(
        (dpto) => dpto.DESCR === value.Provincia.toUpperCase()
      );
      if (currentProvince) {
        let currentDistrict;
        if (value.Distrito) {
          currentDistrict = (district as District[]).find(
            (dist) =>
              dist.MBO_PROVINCIA === currentProvince.MBO_PROVINCIA &&
              dist.DESCR === value.Distrito.toUpperCase()
          );
        }
        if (currentDistrict) {
          setValue({
            ...value,
            Provincia: currentProvince.MBO_PROVINCIA,
            Distrito: currentDistrict?.MBO_DISTRITO
          });
        } else {
          setValue({ ...value, Provincia: currentProvince.MBO_PROVINCIA });
        }
        return (district as District[])
          .filter(
            (prov) =>
              prov.state === currentProvince.state &&
              prov.MBO_PROVINCIA === currentProvince.MBO_PROVINCIA
          )
          .map((item) => ({
            id: item.MBO_DISTRITO,
            description: TextHelper.capitalize(item.DESCR)
          }));
      }
    }
    if (distrito !== '') {
      return (district as District[])
        .filter(
          (item) =>
            item.state === distrito.Departamento &&
            item.MBO_PROVINCIA === distrito.Provincia
        )
        .map((item) => ({
          id: item.MBO_DISTRITO,
          description: TextHelper.capitalize(item.DESCR)
        }));
    }
    return [];
  }

  const mobilityMessage = () => {
    if (profileData?.utpFlag?.trim() === 'Y') {
      return 'Si tengo movilidad personal';
    }
    return 'No tengo movilidad personal';
  };

  const types: any = {
    EMAIL: {
      label: 'Correo',
      placeholder: profile?.email?.length
        ? profile?.email?.toLowerCase()
        : 'Ingresa tu correo',
      type: 'email',
      value: {
        Email:
          profile?.email === null || profile?.email === undefined
            ? ''
            : profile?.email?.toLowerCase()
      }
    },
    PHONE: {
      label: 'Celular',
      placeholder: profile?.phone?.length
        ? profile?.phone?.replace(/^51/, '')
        : 'Ingresa tu celular',
      type: 'phone',
      value: {
        Phone:
          profile?.phone === null || profile?.phone === undefined
            ? ''
            : profile?.phone?.replace(/^51/, '')
      }
    },
    NAMES: {
      label: 'Nombres',
      value: {
        Nombre: fullName.firstName
      }
    },
    SURNAMES: {
      label: 'Apellidos',
      value: {
        Nombre: fullName.lastName
      }
    },
    DOCUMENT_NUMBER: {
      label: 'Documento de identidad',
      value: {
        NumeroDocumento: profileData?.nationalId?.trim()
      }
    },
    BIRTHDATE: {
      label: 'Fecha de nacimiento',
      value: {
        FechaNacimiento: formatDate(profileData?.birthdate?.trim())
      }
    },
    CIVIL_STATUS: {
      label: 'Estado Civil',
      placeholder: {
        placeholderCivil:
          profileData?.maritalStatusDescr?.trim().length === 0 ||
          profileData?.maritalStatusDescr === null
            ? 'Selecciona tu estado civil'
            : profileData?.maritalStatusDescr?.trim()
      },
      value: {
        EstadoCivil:
          profileData?.maritalStatus === null ||
          profileData?.maritalStatus === undefined
            ? ''
            : profileData?.maritalStatus?.trim()
      },
      description: {
        EstadoCivil:
          profileData?.maritalStatusDescr === null ||
          profileData?.maritalStatusDescr === undefined
            ? ''
            : profileData?.maritalStatusDescr?.trim()
      }
    },
    HOME_ADDRESS: {
      label: 'Dirección de domicilio',
      placeholder: {
        placeholderDireccion: 'Ingresa tu dirección',
        placeholderPais:
          profileData?.countryDescr === null ||
          profileData?.countryDescr?.trim().length === 0
            ? 'Selecciona tu pais'
            : profileData?.countryDescr?.trim(),
        placeholderDepartamento:
          profileData?.stateDescr === null ||
          profileData?.stateDescr?.trim().length === 0
            ? 'Selecciona tu departamento'
            : TextHelper.capitalize(profileData?.stateDescr)?.trim(),
        placeholderProvincia:
          profileData?.countyDescr === null ||
          profileData?.countyDescr?.trim().length === 0
            ? 'Selecciona tu provincia'
            : TextHelper.capitalize(profileData?.countyDescr?.trim()),
        placeholderDistrito:
          profileData?.cityDescr === null ||
          profileData?.cityDescr?.trim().length === 0
            ? 'Selecciona tu distrito'
            : TextHelper.capitalize(profileData?.cityDescr?.trim())
      },
      value: {
        Direccion:
          profileData?.address1 === null || profileData?.address1 === undefined
            ? ''
            : profileData?.address1?.trim(),
        Distrito:
          profileData?.cityDescr === null ||
          profileData?.cityDescr === undefined
            ? ''
            : TextHelper.capitalize(profileData?.cityDescr)?.trim(),
        Provincia:
          profileData?.countyDescr === null ||
          profileData?.countyDescr === undefined
            ? ''
            : TextHelper.capitalize(profileData?.countyDescr)?.trim(),
        Departamento:
          profileData?.stateDescr === null ||
          profileData?.stateDescr === undefined ||
          profileData?.stateDescr === ' '
            ? ''
            : TextHelper.capitalize(profileData?.stateDescr)?.trim(),
        Pais:
          profileData?.countryDescr === null ||
          profileData?.countryDescr === undefined ||
          profileData?.countryDescr === ' '
            ? ''
            : profileData?.countryDescr?.trim()
      }
    },
    EMERGENCY_CONTACT: {
      label: 'Contacto de emergencia',
      placeholder: {
        placeholderNombre:
          (profileData?.contactName?.trim().length === 0 ||
            profileData?.contactName?.trim() !== null) &&
          'Ingresa el nombre del contacto',
        placeholderParentesco: profileData?.relationship?.trim().length
          ? profileData?.relationshipDescr
          : 'Selecciona',
        placeholderNumero:
          (profileData?.emergencyPhone?.trim().length === 0 ||
            profileData?.emergencyPhone?.trim() !== null) &&
          'Ingresa el número del contacto'
      },
      value: {
        NombreContacto:
          profileData?.contactName === null ||
          profileData?.contactName === undefined
            ? ''
            : profileData?.contactName?.trim(),
        Parentesco:
          profileData?.relationship === null ||
          profileData?.relationship === undefined
            ? ''
            : profileData?.relationship?.trim(),
        NumeroContacto:
          profileData?.emergencyPhone === null ||
          profileData?.emergencyPhone === undefined
            ? ''
            : profileData?.emergencyPhone?.trim()
      },
      description: {
        NombreContacto:
          profileData?.contactName === null ||
          profileData?.contactName === undefined
            ? ''
            : profileData?.contactName?.trim(),
        Parentesco:
          profileData?.relationshipDescr === null ||
          profileData?.relationshipDescr === undefined
            ? ''
            : profileData?.relationshipDescr?.trim(),
        NumeroContacto:
          profileData?.emergencyPhone === null ||
          profileData?.emergencyPhone === undefined
            ? ''
            : profileData?.emergencyPhone?.trim()
      }
    },
    TYPE_MOBILITY: {
      label: 'Movilidad',
      placeholder: {
        placeholderTienes:
          profileData?.utpFlag === null ||
          profileData?.utpFlag?.trim().length === 0
            ? 'Ingresa'
            : profileData?.utpFlag,
        placeholderTipo:
          profileData?.tipoMovilidadDescr === null ||
          profileData?.tipoMovilidadDescr?.trim().length === 0
            ? 'Selecciona el tipo'
            : profileData?.tipoMovilidadDescr,
        placeholderPlaca: 'Ingresa tu placa'
      },
      value: {
        TienesMovilidadParticular:
          profileData?.utpFlag === null || profileData?.utpFlag === undefined
            ? ''
            : profileData?.utpFlag?.trim(),
        TipoMovilidad:
          profileData?.tipoMovilidadDescr === null ||
          profileData?.tipoMovilidadDescr === undefined
            ? ''
            : profileData?.tipoMovilidadDescr?.trim(),
        NumeroPlaca:
          profileData?.utpNroPlaca === null ||
          profileData?.utpNroPlaca === undefined
            ? ''
            : profileData?.utpNroPlaca?.trim()
      },
      description: {
        TienesMovilidadParticular:
          profileData?.utpFlag === null || profileData?.utpFlag === undefined
            ? ''
            : mobilityMessage(),
        TipoMovilidad:
          profileData?.tipoMovilidadDescr === null ||
          profileData?.tipoMovilidadDescr === undefined
            ? ''
            : profileData?.tipoMovilidadDescr?.trim(),
        NumeroPlaca:
          profileData?.utpNroPlaca === null ||
          profileData?.utpNroPlaca === undefined
            ? ''
            : profileData?.utpNroPlaca?.trim()
      }
    }
  };

  const [typeSelected, setTypeSelected] = useState(types);

  const showCourage: any = () => {
    if (
      Object.values(types[inputType].value).some(
        (v) => typeof v === 'string' && v.length > 0
      )
    ) {
      return Object.values(
        types[inputType]?.description
          ? types[inputType]?.description
          : types[inputType].value
      )
        .filter((v) => typeof v === 'string' && v.length > 0)
        .join(', ');
    }
    if (
      ['DOCUMENT_NUMBER', 'BIRTHDATE', 'NAMES', 'SURNAMES'].includes(inputType)
    ) {
      return 'No se ha registrado';
    }
    return `Ingresa tu ${types[inputType].label.toLowerCase()}`;
  };

  useEffect(() => {
    const { input } = actionState;

    setHasPermission(false);

    setIsActive(input === inputType);
    setTextPermission('');

    if (input === 'DEFAULT') {
      setIsDisabled(false);
      return;
    }

    setIsDisabled(input !== inputType);
  }, [actionState, profile, profileData]);

  const [actionsCount, setActionsCount] = useState(true);

  const obtenerMovilidad = (val: any) =>
    val.TienesMovilidadParticular === 'N'
      ? ' '
      : val.TipoMovilidad === 'Otro' || val.TipoMovilidad === 'O'
      ? 'O'
      : val.TipoMovilidad === 'Automóvil' || val.TipoMovilidad === 'A'
      ? 'A'
      : val.TipoMovilidad === 'Motocicleta' || val.TipoMovilidad === 'M'
      ? 'M'
      : val.TipoMovilidad === 'Bicicleta' || val.TipoMovilidad === 'B'
      ? 'B'
      : val.TipoMovilidad;

  const onSendData = (hasAction: boolean) => {
    let formatBody;
    if (hasAction) {
      if (inputType === 'PHONE' || inputType === 'EMAIL') {
        controllerActionState({
          action: 'CONFIRM',
          requests: null,
          newValue: value.Phone || value.Email,
          // input: actionState.input === inputType ? 'DEFAULT' : inputType,
          input: inputType,
          status: 'DEFAULT'
        });
        return;
      }
    }

    if (inputType === 'CIVIL_STATUS') {
      formatBody = [
        {
          SectionCode: 'EC',
          Fields: [
            {
              Field: 'EstadoCivil',
              Value: value.EstadoCivil
            }
          ]
        }
      ];
    }

    if (inputType === 'HOME_ADDRESS') {
      formatBody = [
        {
          SectionCode: 'D',
          Fields: [
            {
              Field: 'Direccion',
              Value: value.Direccion === '' ? ' ' : value.Direccion
            },
            {
              Field: 'Pais',
              Value: value.Pais
            },
            {
              Field: 'Departamento',
              Value: value.Pais === 'PER' ? value.Departamento : ' '
            },
            {
              Field: 'Provincia',
              Value: value.Pais === 'PER' ? value.Provincia : ' '
            },
            {
              Field: 'Distrito',
              Value: value.Pais === 'PER' ? value.Distrito : ' '
            }
          ]
        }
      ];
    }

    if (inputType === 'TYPE_MOBILITY') {
      formatBody = [
        {
          SectionCode: 'M',
          Fields: [
            {
              Field: 'TienesMovilidadParticular',
              Value: value.TienesMovilidadParticular
            },
            {
              Field: 'TipoMovilidad',
              Value: obtenerMovilidad(value)
            },
            {
              Field: 'NumeroPlaca',
              Value:
                value.TienesMovilidadParticular === 'N' ||
                value.NumeroPlaca === ''
                  ? ' '
                  : value.NumeroPlaca
            }
          ]
        }
      ];
    }

    if (inputType === 'EMERGENCY_CONTACT') {
      formatBody = [
        {
          SectionCode: 'CE',
          Fields: [
            {
              Field: 'NombreContacto',
              Value: value.NombreContacto === '' ? ' ' : value.NombreContacto
            },
            {
              Field: 'Parentesco',
              Value: value.Parentesco
            },
            {
              Field: 'NumeroContacto',
              Value: value.NumeroContacto === '' ? ' ' : value.NumeroContacto
            }
          ]
        }
      ];
    }

    setFormChanges(JSON.stringify(formatBody));

    if (actionsCount && formatBody && isActive) {
      setInitialForm(JSON.stringify(formatBody));
      setActionsCount(false);
    }

    if (hasAction) {
      setActionsCount(true);

      controllerActionState({
        action: 'CONFIRM',
        requests: formatBody,
        // input: actionState.input === inputType ? 'DEFAULT' : inputType,
        input: inputType,
        status: 'DEFAULT'
      });
    }
  };

  const getFieldLabel = (label: string) => {
    switch (label) {
      case 'CIVIL_STATUS':
        return 'Civilstatus';
      case 'TYPE_MOBILITY':
        return 'Mobility';
      case 'EMERGENCY_CONTACT':
        return 'Emergencycontact';
      case 'HOME_ADDRESS':
        return 'HomeAddress';
      default:
        return '';
    }
  };

  useEffect(() => {
    onSendData(false);
  }, [value]);

  useEffect(() => {
    if (isActive) {
      setValue(types[inputType].value);
      return;
    }

    setValue('');
  }, [isActive]);

  const handlerConfirm = () => {
    const label = getFieldLabel(inputType);
    GoogleAnalytics.writeLog(`portal-profile-clickSave${label}`, 'profile');
    onSendData(true);
  };

  const handlerToggle = () => {
    if (isActive) {
      if (actionState.input === 'PHONE') {
        if (value?.Phone && value?.Phone === profile.phone) {
          controllerActionState({
            action: 'DEFAULT',
            input: 'DEFAULT',
            status: 'DEFAULT',
            newValue: '',
            oldValue: ''
          });

          setOpenModal(false);
          return;
        }

        setOpenModal(true);
        return;
      }

      if (actionState.input === 'EMAIL') {
        if (value?.Email && value?.Email === profile.email.toLowerCase()) {
          controllerActionState({
            action: 'DEFAULT',
            input: 'DEFAULT',
            status: 'DEFAULT',
            newValue: '',
            oldValue: ''
          });

          setOpenModal(false);
          return;
        }
        setOpenModal(true);
        return;
      }

      if (formChanges === initialForm) {
        controllerActionState({
          action: 'DEFAULT',
          input: 'DEFAULT',
          status: 'DEFAULT',
          newValue: '',
          oldValue: ''
        });

        setOpenModal(false);
        return;
      }
    }

    if (actionState.input === inputType) {
      setOpenModal(true);
      return;
    }

    if (inputType === 'TYPE_MOBILITY') {
      GoogleAnalytics.writeLog(`portal-profile-clickEditMobility`, 'profile');
      if (!isLoadingMasters) {
        dispatch(
          getProfileMastersDataActions.getProfileMastersData('MOBILITY_TYPE')
        );
      }
    }

    if (inputType === 'CIVIL_STATUS') {
      GoogleAnalytics.writeLog(
        `portal-profile-clickEditCivilstatus`,
        'profile'
      );
      if (!isLoadingMasters) {
        dispatch(
          getProfileMastersDataActions.getProfileMastersData('MARITAL_STATUS')
        );
      }
    }

    if (inputType === 'EMERGENCY_CONTACT') {
      GoogleAnalytics.writeLog(
        `portal-profile-clickEditEmergencycontact`,
        'profile'
      );
      if (!isLoadingMasters) {
        dispatch(
          getProfileMastersDataActions.getProfileMastersData('RELATIONSHIP')
        );
      }
    }

    controllerActionState({
      input: actionState.input === inputType ? 'DEFAULT' : inputType,
      status: 'DEFAULT'
    });
  };

  const onSetIsValid: any = (v: boolean) => {
    if (actionState.input === 'EMERGENCY_CONTACT') {
      if (
        value.NombreContacto === '' ||
        value.NumeroContacto === '' ||
        value.Parentesco === ''
      ) {
        setIsValid(true);
      }
    } else {
      setIsValid(v && actionState.oldValue !== value);
    }
  };

  const adapterReponse = (
    dataResponse: MaritalStatus[]
  ): AdaptedMaritalStatus[] =>
    dataResponse?.map((item) => ({
      id: item.id,
      description: item.name
    }));

  const adapterPais = (dataResponse: MaritalPais[]): AdaptedMaritalStatus[] =>
    dataResponse?.map((item) => ({
      id: item.COUNTRY,
      description: TextHelper.capitalize(item.DESCR)
    }));

  const adapterDepartamento = (
    dataResponse: MaritalDepartamento[]
  ): AdaptedMaritalStatus[] =>
    dataResponse?.map((item) => ({
      id: item.state,
      description: TextHelper.capitalize(item.DESCR)
    }));

  const onChangeSelect = (e: any) => {
    if (e.name === 'Departamento') {
      setTypeSelected({
        ...typeSelected,
        HOME_ADDRESS: {
          ...typeSelected.HOME_ADDRESS,
          placeholder: {
            ...typeSelected.HOME_ADDRESS.placeholder,
            placeholderProvincia: 'Selecciona tu provicia',
            placeholderDistrito: 'Selecciona tu distrito'
          }
        }
      });

      setValue((v: any) => ({
        ...v,
        [e.name]: e.value,
        Provincia: '',
        Distrito: ''
      }));
    }

    if (e.name === 'TipoMovilidad') {
      setTypeSelected({
        ...typeSelected,
        TYPE_MOBILITY: {
          ...typeSelected.TYPE_MOBILITY,
          placeholder: {
            ...typeSelected.TYPE_MOBILITY.placeholder,
            placeholderPlaca: 'Ingresa tu placa'
          }
        }
      });

      setErrorForm({ NumeroPlaca: false });

      setValue((v: any) => ({
        ...v,
        [e.name]: e.value,
        NumeroPlaca: ''
      }));
    }

    if (e.name === 'Provincia') {
      setTypeSelected({
        ...typeSelected,
        HOME_ADDRESS: {
          ...typeSelected.HOME_ADDRESS,
          placeholder: {
            ...typeSelected.HOME_ADDRESS.placeholder,
            placeholderDistrito: 'Selecciona tu distrito'
          }
        }
      });

      setValue((v: any) => ({
        ...v,
        [e.name]: e.value,
        Provincia: '',
        Distrito: ''
      }));
    }

    if (e.value || e.value === '') {
      setValue((v: any) => ({ ...v, [e.name]: e.value }));
      setIsValid(defaultValue !== e.value);
    }
  };

  const onChangeInput = (e: any, name: string) => {
    if (e === '') {
      setValue({ ...value, [name]: '' });
      return;
    }

    if (e !== '') {
      setValue({ ...value, [name]: e });
    }
  };

  return (
    <div>
      <SInputToggle isActive={isActive}>
        <SInputToggleLabel
          isActive={isActive}
          isDisabled={isDisabled}
          hasValue={Object.values(types[inputType].value).some(
            (v) => typeof v === 'string' && v.length > 0
          )}
        >
          <div>
            <p>{types[inputType].label}</p>
            <p>{showCourage()}</p>
          </div>
          <button
            disabled={isDisabled}
            type="button"
            onClick={handlerToggle}
            data-testid="input-toggle-button"
          >
            {hasActionCrud && <span>{isActive ? 'Cancelar' : 'Editar'}</span>}
          </button>
        </SInputToggleLabel>
        {isActive && (
          <SInputToggleInput>
            {inputType === 'PHONE' && (
              <Input
                name="Phone"
                placeholder="Ingresa tu celular"
                value={value.Phone || ''}
                setIsValid={onSetIsValid}
                setValue={(e) => onChangeInput(e, 'Phone')}
                type="phone"
              />
            )}
            {inputType === 'EMAIL' && (
              <Input
                name="Email"
                placeholder="Ingresa tu correo"
                value={value.Email || ''}
                setIsValid={onSetIsValid}
                setValue={(e) => onChangeInput(e, 'Email')}
                type="email"
              />
            )}
            {inputType === 'CIVIL_STATUS' && (
              <Select
                name="EstadoCivil"
                placeholder={types[inputType].placeholder.placeholderCivil}
                value={value?.EstadoCivil}
                setFieldValue={(e) => onChangeSelect(e)}
                optionsList={adapterReponse(data)}
                validateForm={0}
              />
            )}
            {inputType === 'HOME_ADDRESS' && (
              <>
                <Row>
                  <InputText
                    name="Direccion"
                    placeholder={
                      types[inputType].placeholder.placeholderDireccion
                    }
                    value={value.Direccion}
                    setFieldValue={(e) => onChangeSelect(e)}
                    setFieldError={(e) =>
                      setErrorForm({ ...errorForm, Direccion: e.value })
                    }
                    showCount
                    validateOnStart={false}
                    validateForm={0}
                    maxLength={30}
                    nameLabel={'Dirección'}
                  />
                  <Select
                    name="Pais"
                    placeholder={types[inputType].placeholder.placeholderPais}
                    value={value?.Pais}
                    setFieldValue={(e) => onChangeSelect(e)}
                    optionsList={adapterPais(country)}
                    validateForm={0}
                    nameLabel={'Pais'}
                    fontWeight
                  />
                </Row>
                {value.Pais === 'PER' || value.Pais === 'Perú' ? (
                  <Row>
                    <Select
                      name="Departamento"
                      placeholder="Selecciona tu departamento"
                      value={value.Departamento}
                      setFieldValue={(e) => onChangeSelect(e)}
                      optionsList={adapterDepartamento(
                        getDepartmentsByCountry(value.Pais)
                      )}
                      validateForm={0}
                      nameLabel={'Departamento'}
                      fontWeight
                    />
                    <Select
                      name="Provincia"
                      placeholder="Selecciona tu provincia"
                      value={value.Provincia}
                      setFieldValue={(e) => onChangeSelect(e)}
                      optionsList={getProvinceByDepartments(value.Departamento)}
                      validateForm={0}
                      nameLabel={'Provincia'}
                      fontWeight
                    />
                    <Select
                      name="Distrito"
                      placeholder="Selecciona tu distrito"
                      value={value.Distrito}
                      setFieldValue={(e) => onChangeSelect(e)}
                      optionsList={getDistrictByProvince(value)}
                      validateForm={0}
                      nameLabel={'Distrito'}
                      fontWeight
                    />
                  </Row>
                ) : null}
              </>
            )}
            {inputType === 'EMERGENCY_CONTACT' && (
              <>
                <InputText
                  name="NombreContacto"
                  placeholder={types[inputType].placeholder.placeholderNombre}
                  value={value?.NombreContacto}
                  setFieldValue={(e) => onChangeSelect(e)}
                  setFieldError={(e) => {
                    setErrorForm({ ...errorForm, NombreContacto: e.value });
                  }}
                  showCount
                  validateOnStart={false}
                  validateForm={0}
                  maxLength={20}
                  nameLabel={'Nombre del contacto'}
                />
                <p
                  style={{
                    fontFamily: 'Lato',
                    fontWeight: '400',
                    fontSize: '14px',
                    lineHeight: '20px',
                    paddingBottom: '8px'
                  }}
                >
                  Parentesco
                </p>
                <Select
                  name="Parentesco"
                  placeholder={
                    types[inputType].placeholder.placeholderParentesco
                  }
                  value={value.Parentesco}
                  setFieldValue={(e) => onChangeSelect(e)}
                  optionsList={adapterReponse(data)}
                  validateForm={0}
                />
                <p
                  style={{
                    fontFamily: 'Lato',
                    fontWeight: '400',
                    fontSize: '14px',
                    lineHeight: '20px',
                    paddingBottom: '8px'
                  }}
                >
                  Número del contacto
                </p>
                <InputPhone
                  value={value?.NumeroContacto || ''}
                  name="NumeroContacto"
                  placeholder={types[inputType].placeholder.placeholderNumero}
                  maxLength={11}
                  // setFieldValue={function ({ name, value }: any): void {
                  //   throw new Error('Function not implemented.');
                  // }}
                  setFieldValue={(e) =>
                    onChangeInput(e.value, 'NumeroContacto')
                  }
                  validateForm={0}
                  setFieldError={(e) => {
                    setErrorForm({ ...errorForm, NumeroContacto: e.value });
                  }}
                />
              </>
            )}
            {inputType === 'TYPE_MOBILITY' && (
              <>
                <p
                  style={{
                    fontWeight: '400',
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontFamily: 'Lato'
                  }}
                >
                  ¿Tienes movilidad personal?
                </p>
                <Select
                  name="TienesMovilidadParticular"
                  placeholder="Ingresa"
                  value={value.TienesMovilidadParticular}
                  setFieldValue={(e) => onChangeSelect(e)}
                  optionsList={[
                    { id: 'Y', description: 'Si tengo movilidad personal' },
                    { id: 'N', description: 'No tengo movilidad personal' }
                  ]}
                  validateForm={0}
                />
                {value?.TienesMovilidadParticular?.trim() === 'Y' ? (
                  <>
                    <Select
                      name="TipoMovilidad"
                      placeholder={types[inputType].placeholder.placeholderTipo}
                      value={value.TipoMovilidad}
                      setFieldValue={(e) => onChangeSelect(e)}
                      optionsList={adapterReponse(data)}
                      validateForm={0}
                      nameLabel={'Tipo de movilidad'}
                      fontWeight
                    />
                    <InputText
                      name="NumeroPlaca"
                      placeholder={
                        value.TipoMovilidad === 'B' ||
                        value.TipoMovilidad === 'O' ||
                        value.TipoMovilidad === 'Otro'
                          ? 'Ingresa las características'
                          : types[inputType].placeholder.placeholderPlaca
                      }
                      value={value.NumeroPlaca}
                      setFieldValue={(e) => onChangeSelect(e)}
                      setFieldError={(e) =>
                        setErrorForm({ ...errorForm, NumeroPlaca: e.value })
                      }
                      showCount
                      validateOnStart={false}
                      validateForm={0}
                      maxLength={9}
                      nameLabel={
                        value.TipoMovilidad === 'B' ||
                        value.TipoMovilidad === 'O' ||
                        value.TipoMovilidad === 'Otro' ||
                        value.TipoMovilidad === 'Bicicleta'
                          ? 'Características'
                          : types[inputType].placeholder.placeholderPlaca
                      }
                    />
                  </>
                ) : null}
              </>
            )}
            <ReusableButton
              disabled={
                Object.keys(errorForm)?.some((k) => errorForm[k]) ||
                Object.keys(value)?.every((k) => !value[k]) ||
                formChanges === initialForm
              }
              onClick={handlerConfirm}
              text={
                ['PHONE', 'EMAIL'].includes(inputType) ? 'Validar' : 'Guardar'
              }
              type="button"
            />
          </SInputToggleInput>
        )}
      </SInputToggle>
      {hasPermission && (
        <ReusableInfo
          backgroundColor="#fabd00"
          iconColor="#161D1F"
          text={`Espera 24 horas después de la última actualización para hacer modificaciones en tu *${textPermission}*`}
          textColor="#161D1F"
          style={{ marginTop: '24px' }}
        />
      )}

      <ModalConfirmation
        show={openModal}
        handleClose={() => {
          setOpenModal(false);
        }}
      >
        <ContentModalTwoButtons
          align="center"
          img={null}
          title={`</br> ¿Deseas cancelar y perder los cambios?`}
          subtitle={`Si cancelas, cualquier edición realizada no se guardará.`}
          showInfo={false}
          textButton1="Sí, cancelar"
          textButton2="Seguir editando"
          onClickButton1={() => {
            controllerActionState({
              action: 'DEFAULT',
              input: 'DEFAULT',
              status: 'DEFAULT',
              newValue: '',
              oldValue: ''
            });
            setOpenModal(false);
          }}
          onClickButton2={() => {
            setOpenModal(false);
          }}
        />
      </ModalConfirmation>
    </div>
  );
};

export default InputToggle;
